import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill } from '@/components/px/StatusPill'
import { fetchTenantPolicy } from '@/features/org/orgSlice'
import {
  selectPolicyCache,
  selectPolicyError,
  selectTenantPolicy
} from '@/features/org/orgSelectors'
import { useAppDispatch, useAppSelector } from '@/store'

/** The KNOWN policy facts, in display order; anything else the server sent
 * still renders (passthrough) — this list is presentation, not a filter. */
const KNOWN_KEYS: readonly { key: string; labelKey: string }[] = [
  { key: 'region', labelKey: 'policy.region' },
  { key: 'dataClassification', labelKey: 'policy.dataClassification' },
  { key: 'retentionDays', labelKey: 'policy.retentionDays' },
  { key: 'spendCapMinutes', labelKey: 'policy.spendCapMinutes' },
  { key: 'clientSelfOnboarding', labelKey: 'policy.clientSelfOnboarding' },
  { key: 'gateAgeingThresholdDays', labelKey: 'policy.gateAgeingThresholdDays' }
]

/** Server-identity keys the read-only table never renders as facts. */
const HIDDEN_KEYS = new Set(['id', 'orgId', 'organisationId', 'slug', 'createdAt', 'lastUpdate'])

/**
 * The tenant policy panel (PX-W6.2, AC-4) — read-only by contract (no write
 * verb exists). The rows render what the server sent; booleans render On/Off.
 */
export function TenantPolicyPanel() {
  const { t } = useTranslation('orgSettings')
  const dispatch = useAppDispatch()

  const policy = useAppSelector(selectTenantPolicy)
  const cache = useAppSelector(selectPolicyCache)
  const error = useAppSelector(selectPolicyError)

  useEffect(() => {
    void dispatch(fetchTenantPolicy())
  }, [dispatch])

  const rows: { key: string; label: string; value: string }[] = []
  if (policy !== null) {
    for (const { key, labelKey } of KNOWN_KEYS) {
      const value = policy[key]
      if (value === undefined || value === null) continue
      rows.push({ key, label: t(labelKey), value: renderValue(value, t) })
    }
    for (const [key, value] of Object.entries(policy)) {
      if (HIDDEN_KEYS.has(key)) continue
      if (rows.some((row) => row.key === key)) continue
      if (value === null || value === undefined || typeof value === 'object') continue
      rows.push({ key, label: key, value: renderValue(value, t) })
    }
  }

  return (
    <section
      aria-labelledby="tenant-policy-label"
      className="rounded-lg border border-border bg-card px-4 py-4"
    >
      <div id="tenant-policy-label" className="flex flex-wrap items-center gap-2">
        <SectionLabel>{t('policy.title')}</SectionLabel>
        <span className="text-xs text-muted-foreground">{t('policy.caption')}</span>
      </div>
      <div className="mt-3">
        {cache.loading ? (
          <LoadingPanel />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/tenant-policy" />
        ) : error !== null ? (
          <ErrorPanel error={error} onRetryClicked={() => void dispatch(fetchTenantPolicy())} />
        ) : rows.length === 0 ? (
          <LoadingPanel />
        ) : (
          <dl className="grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
            {rows.map((row) => (
              <div key={row.key} className="flex gap-2">
                <dt className="text-muted-foreground">{row.label}</dt>
                <dd>
                  {row.key === 'dataClassification' ? (
                    <StatusPill tone="decided">{row.value}</StatusPill>
                  ) : (
                    <span className="text-foreground">{row.value}</span>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    </section>
  )
}

/** Render one policy value: booleans On/Off, everything else as text. */
function renderValue(value: unknown, t: (key: string) => string): string {
  if (typeof value === 'boolean') return value ? t('policy.on') : t('policy.off')
  return String(value)
}
